import { gsap } from "gsap";
import { lenisStop, lenisStart } from "./smoothScroll";

/**
 * Mobile menu off-canvas.
 *
 * - Slide dall'alto (full-height) all'apertura
 * - Hamburger button morpha in X
 * - Body-lock anti-scroll (con fix iOS Safari)
 * - Focus trap (Tab non esce dal panel)
 * - ESC chiude e restituisce focus al toggle
 * - Click sull'overlay close (non rilevante full-height ma il pattern resta)
 * - GSAP timeline con stagger sulle voci menu
 *
 * Markup:
 *  - <button data-mobile-menu-toggle> nel header
 *  - <aside data-mobile-menu> nel partial mobile-menu.twig
 *  - <button data-mobile-menu-close> dentro al pannello
 */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let isOpen = false;
let savedScrollY = 0;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const lockBodyScroll = () => {
  // iOS Safari: overflow:hidden non basta; uso position:fixed + tracking scrollY
  savedScrollY = window.scrollY;
  document.body.style.position = "fixed";
  document.body.style.top = `-${savedScrollY}px`;
  document.body.style.width = "100%";
  document.body.classList.add("mobile-menu-open");
};

const unlockBodyScroll = () => {
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.width = "";
  document.body.classList.remove("mobile-menu-open");
  window.scrollTo(0, savedScrollY);
};

const trapFocus = (panel, event) => {
  if (event.key !== "Tab") return;

  const focusableEls = panel.querySelectorAll(FOCUSABLE_SELECTOR);
  if (!focusableEls.length) return;

  const first = focusableEls[0];
  const last = focusableEls[focusableEls.length - 1];
  const active = document.activeElement;

  if (event.shiftKey && active === first) {
    last.focus();
    event.preventDefault();
  } else if (!event.shiftKey && active === last) {
    first.focus();
    event.preventDefault();
  }
};

const animateOpen = (panel) => {
  panel.classList.add("is-animatable");
  const items = panel.querySelectorAll(".mobile-menu__item");
  const footer = panel.querySelector(".mobile-menu__footer");

  if (prefersReducedMotion()) {
    gsap.set(panel, { y: "0%" });
    gsap.set([...items, footer].filter(Boolean), { opacity: 1, y: 0 });
    return;
  }

  const tl = gsap.timeline();

  // Panel slide dall'alto
  tl.fromTo(panel,
    { yPercent: -100 },
    { yPercent: 0, duration: 0.4, ease: "power3.out" },
    0
  );

  // Voci menu stagger
  if (items.length) {
    tl.fromTo(items,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.4, ease: "power2.out", stagger: 0.05 },
      0.2
    );
  }

  // Footer (lang switcher)
  if (footer) {
    tl.fromTo(footer,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
      0.4
    );
  }
};

const animateClose = (panel, onComplete) => {
  if (prefersReducedMotion()) {
    gsap.set(panel, { yPercent: -100 });
    onComplete();
    return;
  }

  gsap.to(panel, {
    yPercent: -100,
    duration: 0.3,
    ease: "power3.in",
    onComplete,
  });
};

const openMenu = (panel, toggle) => {
  if (isOpen) return;
  isOpen = true;

  // 1. Rendi visibile (display:none → flex) PRIMA di animare
  panel.classList.add("is-open");
  panel.setAttribute("aria-hidden", "false");
  toggle.setAttribute("aria-expanded", "true");
  toggle.classList.add("is-open");

  lockBodyScroll();
  lenisStop();

  // 2. Forza reflow così il browser applica display:flex prima del transform
  void panel.offsetHeight;

  // 3. Animazione GSAP (panel parte da yPercent:-100, va a 0)
  animateOpen(panel);

  // Focus al primo elemento focusable (close button)
  const firstFocusable = panel.querySelector(FOCUSABLE_SELECTOR);
  if (firstFocusable) {
    setTimeout(() => firstFocusable.focus(), 100);
  }
};

const closeMenu = (panel, toggle) => {
  if (!isOpen) return;
  isOpen = false;

  toggle.setAttribute("aria-expanded", "false");
  toggle.classList.remove("is-open");

  animateClose(panel, () => {
    panel.classList.remove("is-open");
    panel.classList.remove("is-animatable");
    panel.setAttribute("aria-hidden", "true");
    unlockBodyScroll();
    lenisStart();
    toggle.focus();
  });
};

const initMobileMenu = () => {
  const toggle = document.querySelector("[data-mobile-menu-toggle]");
  const panel = document.querySelector("[data-mobile-menu]");
  const closeBtn = document.querySelector("[data-mobile-menu-close]");

  if (!toggle || !panel) return;

  // Il panel è display:none da CSS finché .is-open non è applicata.
  // L'animazione GSAP usa fromTo per impostare yPercent:-100 ogni apertura.

  toggle.addEventListener("click", () => {
    if (isOpen) {
      closeMenu(panel, toggle);
    } else {
      openMenu(panel, toggle);
    }
  });

  if (closeBtn) {
    closeBtn.addEventListener("click", () => closeMenu(panel, toggle));
  }

  // ESC: chiude e ridà focus al toggle
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && isOpen) {
      closeMenu(panel, toggle);
    } else if (isOpen) {
      trapFocus(panel, e);
    }
  });

  // Resize: se passo a desktop con menu aperto, lo chiudo per evitare layout broken
  window.addEventListener("resize", () => {
    if (isOpen && window.innerWidth >= 992) {
      closeMenu(panel, toggle);
    }
  });
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMobileMenu);
  } else {
    initMobileMenu();
  }
};